import { useEffect, useState } from "react";
import type { SearchResult } from "@/lib/api";
import { api } from "@/lib/api";
import { useDebounce } from "@/hooks/use-debounce";

interface UseLocationSearchOptions {
  minLength?: number;
  debounceMs?: number;
}

export function useLocationSearch(
  query: string,
  options: UseLocationSearchOptions = {},
) {
  const { minLength = 2, debounceMs = 400 } = options;
  const debouncedQuery = useDebounce(query, debounceMs);

  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < minLength) {
      setResults([]);
      return;
    }

    let cancelled = false;

    const search = async () => {
      setIsSearching(true);
      try {
        const searchResults = await api.searchLocation(debouncedQuery);
        if (!cancelled) {
          setResults(searchResults);
        }
      } catch (error) {
        console.error("Search error:", error);
        if (!cancelled) {
          setResults([]);
        }
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    };

    search();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, minLength]);

  return {
    results,
    setResults,
    isSearching,
  };
}
